import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { formatRands } from "@/lib/order";
import { StaffShell } from "@/components/layout/StaffShell";
import { vendorNav } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ImageUpload } from "@/components/common/ImageUpload";
import { CenterSpinner, EmptyState } from "@/components/common/States";

const EMPTY = { name: "", description: "", price: "", sale_price: "", stock_quantity: 100, image_url: null, is_available: true };

export default function VendorProducts() {
  const qc = useQueryClient();
  const vendor = useQuery({ queryKey: ["my-vendor"], queryFn: () => api("/my/vendor") });
  const products = useQuery({
    queryKey: ["vendor-products", vendor.data?.vendor_id],
    queryFn: () => api(`/products?vendor_id=${vendor.data.vendor_id}&include_unavailable=true`),
    enabled: !!vendor.data?.vendor_id,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, sale_price: p.sale_price || "" });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: form.name,
      description: form.description,
      price: Number(form.price),
      sale_price: form.sale_price ? Number(form.sale_price) : null,
      stock_quantity: Number(form.stock_quantity),
      image_url: form.image_url,
      is_available: form.is_available,
    };
    try {
      if (editing) await api(`/products/${editing.product_id}`, { method: "PATCH", json: payload });
      else await api("/products", { method: "POST", json: payload });
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["vendor-products"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (p) => {
    try {
      await api(`/products/${p.product_id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["vendor-products"] });
      toast.success("Product removed");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <StaffShell
      items={vendorNav}
      roleLabel="Vendor"
      title="Products"
      headerRight={
        <Button className="rounded-full" onClick={openNew} data-testid="add-product-button">
          <Plus className="mr-1 h-4 w-4" /> Add product
        </Button>
      }
    >
      {vendor.isLoading || products.isLoading ? (
        <CenterSpinner />
      ) : !products.data?.length ? (
        <EmptyState title="No products yet" description="Add your first product to start selling." action={<Button className="rounded-full" onClick={openNew}>Add product</Button>} />
      ) : (
        <div className="grid gap-3">
          {products.data.map((p) => (
            <Card key={p.product_id} className="flex items-center gap-4 rounded-2xl p-3" data-testid={`product-row-${p.product_id}`}>
              {p.image_url ? (
                <img src={fileUrl(p.image_url)} alt={p.name} className="h-16 w-16 rounded-xl object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-xl bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-display text-base">{p.name}</p>
                  {!p.is_available && <Badge variant="secondary" className="rounded-full font-normal">Hidden</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">
                  {p.sale_price ? `${formatRands(p.sale_price)} · was ${formatRands(p.price)}` : formatRands(p.price)} · stock {p.stock_quantity}
                </p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(p)} data-testid={`delete-product-${p.product_id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} label="Product photo" testId="product-image-upload" />
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Price (R)</Label>
                <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Sale (R)</Label>
                <Input type="number" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Stock</Label>
                <Input type="number" value={form.stock_quantity} onChange={(e) => set("stock_quantity", e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <Label>Available to customers</Label>
              <Switch checked={form.is_available} onCheckedChange={(v) => set("is_available", v)} data-testid="product-available-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={save} disabled={!form.name || !form.price} data-testid="save-product-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
